import { useEffect } from 'react';
import type { RefObject } from 'react';

import type { FormikErrors, FormikValues } from 'formik/dist/types';

import { smoothFocus } from 'src/app/utils/smoothFocus';

const normalizeFormInputId = (elementId: string, formName?: string) => {
  // Edge case but on the pages we have multiple forms with the same form field names, we needed to
  // add prefixes so we did not have duplicate ids. The error object coming back resembles 'patient_name'
  // while the prefixed name used for ids could be 'doctor_lead_form_patient_name'.
  return formName ? elementId.replace(`${formName}-`, '') : elementId;
};

/**
 * Will focus the form's input on first error if the user clicks submit. This is ideal for forms with a small amount
 * of fields.
 *
 * @see https://webaim.org/techniques/formvalidation/#:~:text=It%20is%20typically%20best%20to%20set%20focus%20to%20the%20first%20control%20that%20needs%20attention.
 * @see https://twitter.com/kylemh_/status/1524172475617222656
 */
export const useFocusOnFirstFormError = (
  /** Used to find the first item positioned in the visual window that matches the errors' id */
  formRef: RefObject<HTMLFormElement>,

  /** From formik, used to match the current error state with the DOM's ids to focus */
  errors: FormikErrors<FormikValues>,

  /** From formik, only should focus when the form is in a submitting state */
  isSubmitting: boolean,

  /** From formik, only should focus when the form is not in a validating state */
  isValidating: boolean,

  /**
   * @deprecated Small is subjective, but certainly if the form requires scrolling, this isn't the ideal use case for
   * this hook. We should instead discuss alternative means of giving all users the chance to accessibly tackle field
   * errors in the form.
   */
  shouldScroll: boolean,

  /** This optional param is provided to delineate between identical forms on the same page */
  formName?: string
) => {
  useEffect(() => {
    // This will execute the below code after the current call stack finishes. In some of our forms,
    // the formRef will be re-initialized and returned as null, causing a race condition. Without
    // this setTimeout, the logged in transform will not focus as formRef will be null and will
    // reach the smoothFocus portion.
    setTimeout(() => {
      if (formRef?.current) {
        // https://github.com/microsoft/TypeScript/issues/5329 Explains below (a rabbit hole tho)
        // @ts-ignore
        for (const element of formRef?.current?.elements) {
          const elementId = element?.id;
          const normalizedElementId = normalizeFormInputId(elementId, formName);

          // For more context on below look here,
          // https://github.com/formium/formik/issues/146#issuecomment-458588962
          if (errors?.hasOwnProperty(normalizedElementId) && isSubmitting && !isValidating) {
            smoothFocus(elementId, 0, elementId, 250, shouldScroll);
            break;
          }
        }
      }
    }, 0);
  }, [errors, formName, formRef, isSubmitting, isValidating, shouldScroll]);
};
